// Sky (art guide §3 Sky/Fog): a gradient dome that meets the material haze exactly at the
// horizon, a warm cloud sea far below the islands (LIBERTY), 3-4 depth layers of pre-fogged
// floating-island / mesa silhouette cards for the poster look, and drifting dust motes.

import * as THREE from 'three';
import { HAZE } from '../materials';
import { PAL } from '../palette';
import { Jit } from './geo';
import { valueNoise } from '../../../sim/core';
import type { DayNight } from './daynight';
import { Celestial, STARFIELD_GLSL } from './celestial';

const DOME_VERT = /* glsl */ `
varying vec3 vDir;
void main() {
  vDir = normalize( position );
  vec4 p = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  gl_Position = p.xyww; // pinned to the far plane
}
`;
const DOME_FRAG = /* glsl */ `
varying vec3 vDir;
uniform vec3 uTop; uniform vec3 uMid; uniform vec3 uHorizon;
uniform float uTownMood; uniform float uTime;
uniform vec3 uTopBlue; uniform vec3 uMidBlue; uniform vec3 uCloudLit; uniform vec3 uCloudShade;
float h21( vec2 p ) { return fract( sin( dot( p, vec2( 127.1, 311.7 ) ) ) * 43758.5453 ); }
float vn( vec2 p ) {
  vec2 i = floor( p ), f = fract( p ); f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( h21( i ), h21( i + vec2( 1, 0 ) ), f.x ), mix( h21( i + vec2( 0, 1 ) ), h21( i + 1.0 ), f.x ), f.y );
}
float fbm3( vec2 p ) { return vn( p ) * 0.55 + vn( p * 2.1 + 3.1 ) * 0.3 + vn( p * 4.3 + 7.7 ) * 0.15; }
float cloudDens( vec2 p ) {
  vec2 w = vec2( vn( p * 1.3 + 5.2 ), vn( p * 1.3 + 9.7 ) ) - 0.5;
  vec2 q = p + w * 0.6;
  float f = vn( q ) * 0.5 + vn( q * 2.03 + 3.1 ) * 0.25 + vn( q * 4.1 + 7.7 ) * 0.14 + vn( q * 8.3 + 1.3 ) * 0.07 + vn( q * 16.7 + 4.4 ) * 0.04;
  float mask = vn( p * 0.33 + 17.0 );
  return f * 0.85 + ( mask - 0.5 ) * 0.55 + 0.02;
}
uniform vec3 uHazeColor; uniform vec3 uSunHazeColor; uniform vec3 uSunDir; uniform vec3 uSunCore;
// day/night (daynight.ts): uSunDir is the KEY light (sun by day, big moon by night), which the
// haze glows toward exactly as the materials do; uSunDisc is the true sun for disc, rays and dusk
uniform float uNight; uniform float uDusk; uniform vec3 uSunDisc; uniform mat3 uSkyRotInv;
${STARFIELD_GLSL}
void main() {
  vec3 d = normalize( vDir );
  float e = d.y;
  float sk = max( dot( d, uSunDir ), 0.0 );
  float s = max( dot( d, uSunDisc ), 0.0 );
  // haze exactly like the materials' far colour, so terrain dissolves into sky without a seam
  vec3 hz = mix( uHazeColor, uSunHazeColor, pow( sk, 5.0 ) * 0.8 );
  float up = smoothstep( 0.0, 0.75, e );
  // print-style: the gradient advances in soft steps
  float stepped = floor( up * 9.0 ) / 9.0;
  up = mix( up, stepped, 0.18 );
  vec3 col = mix( uHorizon, uMid, smoothstep( 0.0, 0.45, up ) );
  col = mix( col, uTop, smoothstep( 0.35, 1.0, up ) );
  col = mix( col, hz, exp( -max( e, 0.0 ) * 9.0 ) * 0.9 );
  // luminous horizon glow: a pale warm band hugging the horizon all the way round
  col = mix( col, vec3( 1.0, 0.93, 0.78 ), exp( -abs( e ) * 14.0 ) * 0.42 * ( 1.0 - 0.6 * uTownMood ) );
  // and a broad, pale, luminous swell over the horizon (strongest toward the sun's side of
  // the sky, still present opposite it) that the valley mountains stand against
  float az = 0.55 + 0.45 * pow( max( dot( normalize( vec3( d.x, 0.0, d.z ) ), normalize( vec3( uSunDisc.x, 0.0, uSunDisc.z ) ) ), 0.0 ), 2.0 );
  col = mix( col, vec3( 1.0, 0.95, 0.84 ), exp( -max( e, 0.0 ) * 5.5 ) * smoothstep( -0.08, 0.02, e ) * 0.5 * az * ( 1.0 - uTownMood ) );
  if ( e < 0.0 ) col = mix( hz, uHazeColor * 0.94, smoothstep( 0.0, -0.4, e ) );
  // painted god rays fanning from the sun through the haze
  vec3 sp = normalize( d - uSunDisc * dot( d, uSunDisc ) );
  vec3 ax = normalize( cross( uSunDisc, vec3( 0.0, 1.0, 0.0 ) ) );
  vec3 ay = cross( ax, uSunDisc );
  float ra = atan( dot( sp, ay ), dot( sp, ax ) );
  float rays = 0.5 + 0.5 * sin( ra * 9.0 + sin( ra * 23.0 ) * 1.3 ) * sin( ra * 5.0 + 1.7 );
  rays = smoothstep( 0.35, 0.95, rays ) * pow( s, 3.0 ) * smoothstep( -0.05, 0.12, e );
  col = mix( col, uSunCore, rays * 0.28 * ( 1.0 - uTownMood ) );
  // town mood (western_town.png): deep blue overhead, warm horizon, painted white cumulus
  if ( uTownMood > 0.001 ) {
    vec3 bcol = mix( uHorizon, uMidBlue, smoothstep( 0.0, 0.32, e ) );
    bcol = mix( bcol, uTopBlue, smoothstep( 0.25, 0.85, e ) );
    // crisp cumulus (western_town.png): discrete puffs gathered by a slow cluster mask, a
    // domain-warped 5-octave body for cauliflower edges, a near-hard rim (AA'd with fwidth),
    // lit cream where the cloud's upper edge faces the sky, blue-grey flat bellies below
    vec2 cp = d.xz / ( max( e, 0.0 ) + 0.22 ) * 2.4 + vec2( uTime * 0.006, uTime * 0.002 );
    vec2 up = normalize( d.xz + 1e-4 ) * 0.24; // toward the horizon in the projected plane = down the sky
    float dn = cloudDens( cp ), dUp = cloudDens( cp - up ), dDn = cloudDens( cp + up * 1.3 );
    float aa = max( fwidth( dn ) * 1.2, 0.004 );
    float cov = smoothstep( 0.6 - aa, 0.6 + aa, dn ) * smoothstep( 0.03, 0.14, e ) * smoothstep( 0.72, 0.42, e );
    // top edges (emptier above) lit, bottoms (emptier below) in shade; interiors in between
    float lit = clamp( 0.55 + ( dn - dUp ) * 3.2 - ( dn - dDn ) * 2.2, 0.0, 1.0 );
    lit = smoothstep( 0.25, 0.75, lit );
    vec3 cc = mix( uCloudShade, uCloudLit, lit );
    // flat, darker belly band along the cloud base
    cc = mix( cc, uCloudShade * 0.9, smoothstep( 0.68, 0.56, dDn ) * 0.45 );
    bcol = mix( bcol, cc, cov );
    bcol = mix( bcol, hz, exp( -max( e, 0.0 ) * 14.0 ) * 0.6 );
    col = mix( col, bcol, smoothstep( 0.0, 0.55, uTownMood ) );
  }
  // ---- sunset / sunrise: fire low on the sun's side, a rose belt opposite, violet overhead
  if ( uDusk > 0.001 ) {
    float toSun = dot( normalize( d.xz + 1e-4 ), normalize( uSunDisc.xz + 1e-4 ) ) * 0.5 + 0.5;
    float low = exp( -max( e, 0.0 ) * 5.0 );
    vec3 dc = mix( vec3( 0.16, 0.12, 0.3 ), vec3( 0.62, 0.3, 0.36 ), smoothstep( 0.75, 0.05, e ) );
    dc = mix( dc, vec3( 1.0, 0.34, 0.1 ), low * pow( toSun, 1.6 ) );
    dc = mix( dc, vec3( 1.0, 0.62, 0.26 ), exp( -abs( e ) * 16.0 ) * pow( toSun, 2.4 ) * 0.9 );
    // streaks of lit cloud on the sun's side (thin horizontal bands, painted)
    float streak = smoothstep( 0.55, 0.8, vn( vec2( atan( d.z, d.x ) * 3.0, e * 38.0 ) ) ) * smoothstep( 0.02, 0.06, e ) * smoothstep( 0.24, 0.1, e );
    dc = mix( dc, mix( vec3( 0.55, 0.22, 0.3 ), vec3( 1.0, 0.55, 0.3 ), pow( toSun, 2.0 ) ), streak * 0.7 );
    if ( e < 0.0 ) dc = mix( hz, uHazeColor * 0.94, smoothstep( 0.0, -0.4, e ) );
    dc = mix( dc, hz, exp( -max( e, 0.0 ) * 12.0 ) * 0.35 );
    col = mix( col, dc, uDusk * 0.92 * ( 1.0 - 0.4 * uTownMood ) );
  }
  // ---- night: indigo dome, luminous haze at the horizon, stars, the nebula band
  if ( uNight > 0.001 ) {
    vec3 nc = mix( vec3( 0.05, 0.075, 0.16 ), vec3( 0.018, 0.03, 0.085 ), smoothstep( 0.0, 0.4, e ) );
    nc = mix( nc, vec3( 0.006, 0.01, 0.034 ), smoothstep( 0.3, 1.0, e ) );
    // airglow just over the horizon, and the last of the twilight on the sun's side
    float toSun = dot( normalize( d.xz + 1e-4 ), normalize( uSunDisc.xz + 1e-4 ) ) * 0.5 + 0.5;
    // a teal-to-dusty-violet airglow band over the whole horizon, and on the sun's side the
    // twilight: rose after sunset, peach before dawn
    float hb = exp( -max( e, 0.0 ) * 7.0 );
    vec3 glow = mix( vec3( 0.2, 0.12, 0.26 ), vec3( 0.08, 0.22, 0.24 ), smoothstep( 0.0, 0.14, e ) ) * hb * 1.5;
    glow += vec3( 1.0, 0.4, 0.2 ) * exp( -max( e, 0.0 ) * 3.6 ) * pow( toSun, 1.6 ) * smoothstep( -0.42, -0.04, uSunDisc.y ) * 1.2;
    vec3 ds = uSkyRotInv * d;
    float px = max( length( fwidth( d ) ), 1e-5 );
    float sv = smoothstep( 0.04, 0.38, e ) * smoothstep( 0.1, 0.8, uNight );
    if ( sv > 0.0 ) {
      nc += starfield( ds, px ) * sv;
    }
    nc = mix( nc, hz, exp( -max( e, 0.0 ) * 11.0 ) * 0.9 );
    if ( e < 0.0 ) nc = mix( hz, uHazeColor * 0.94, smoothstep( 0.0, -0.4, e ) );
    // the glow sits over the haze line, so it lights the sky behind the farthest ranges
    nc += glow * smoothstep( -0.05, 0.0, e );
    col = mix( col, nc, uNight );
  }
  // sun glow and disc (the disc sets: it sinks behind the horizon haze)
  float sunVis = smoothstep( -0.06, 0.02, uSunDisc.y );
  col = mix( col, mix( uSunCore, vec3( 1.0, 0.55, 0.25 ), uDusk ), ( pow( s, 6.0 ) * 0.45 + pow( s, 40.0 ) * 0.4 ) * sunVis );
  col = mix( col, mix( vec3( 1.0, 0.97, 0.88 ), vec3( 1.0, 0.7, 0.42 ), uDusk ), smoothstep( 0.9990, 0.9994, s ) * smoothstep( -0.012, 0.004, e ) * sunVis );
  gl_FragColor = vec4( col, 1.0 );
}
`;

const CLOUD_VERT = /* glsl */ `
varying vec3 vCol; varying vec3 vW; varying float vR;
attribute vec3 color;
uniform float uTime;
void main() {
  vCol = color;
  vR = length( position.xz );
  vec3 p = position;
  p.y += sin( uTime * 0.12 + p.x * 0.01 ) * 2.0 + sin( uTime * 0.09 + p.z * 0.013 ) * 2.0;
  vec4 w = modelMatrix * vec4( p, 1.0 );
  vW = w.xyz;
  gl_Position = projectionMatrix * viewMatrix * w;
}
`;
const CLOUD_FRAG = /* glsl */ `
varying vec3 vCol; varying vec3 vW; varying float vR;
uniform vec3 uHazeColor; uniform vec3 uSunHazeColor; uniform vec3 uSunDir;
uniform vec3 uLit; uniform vec3 uMid; uniform vec3 uShade; uniform float uNight;
void main() {
  float l = vCol.x + vCol.y * 0.25;
  float b1 = smoothstep( 0.22, 0.42, l ), b2 = smoothstep( 0.45, 0.66, l );
  vec3 base = mix( mix( uShade, uMid, b1 ), uLit, b2 );
  vec3 to = vW - cameraPosition;
  float dist = length( to );
  float s = pow( max( dot( to / dist, uSunDir ), 0.0 ), 5.0 );
  vec3 hz = mix( uHazeColor, uSunHazeColor, s * 0.8 );
  // hazier floor overall: a lit cloud hill seen against the hazed floor behind it must not cut a hard arc
  float f = ( smoothstep( 60.0, 700.0, dist ) * 0.72 + 0.26 ) * ( 1.0 - 0.4 * uNight );
  // the far rim dissolves into exactly the dome's below-horizon colour, so no hard arc shows
  float e = to.y / dist;
  vec3 domeLow = mix( hz, uHazeColor * 0.94, smoothstep( 0.0, -0.4, e ) );
  // (the camera far plane is 1400 m: fade out before it clips the floor into a hard arc)
  float rim = max( smoothstep( 750.0, 1290.0, vR ), smoothstep( 950.0, 1380.0, dist ) );
  gl_FragColor = vec4( mix( mix( base, hz, f ), domeLow, rim ), 1.0 );
}
`;

const MOTE_VERT = /* glsl */ `
attribute vec3 aSeed;
uniform float uTime; uniform vec3 uCam; uniform float uBox; uniform float uPx; uniform vec3 uSunDirM; uniform float uNight;
varying float vA;
void main() {
  vec3 drift = vec3( uTime * 0.35, sin( uTime * 0.3 + aSeed.x * 6.0 ) * 0.6, uTime * 0.18 );
  vec3 p = aSeed * uBox + drift;
  p = uCam - 0.5 * uBox + mod( p - ( uCam - 0.5 * uBox ), uBox );
  vec3 e = abs( p - uCam ) / ( 0.5 * uBox );
  vA = smoothstep( 1.0, 0.6, max( e.x, max( e.y, e.z ) ) ) * ( 0.5 + 0.5 * sin( uTime * 1.3 + aSeed.y * 30.0 ) );
  vec4 mv = viewMatrix * vec4( p, 1.0 );
  gl_Position = projectionMatrix * mv;
  gl_PointSize = min( uPx * ( 0.5 + aSeed.z ) * 4.0 / max( -mv.z, 0.5 ), 9.0 );
  // motes only glint where they cross the sun's light: brighter looking toward the sun
  vA *= 0.35 + 0.9 * pow( max( dot( normalize( p - uCam ), uSunDirM ), 0.0 ), 2.0 );
  // at night the motes become fireflies: fewer, slower pulses, low over the grass, lit from within
  if ( uNight > 0.0 ) {
    float fly = step( 0.74, fract( aSeed.x * 7.13 + aSeed.z * 3.1 ) );
    float pulse = smoothstep( 0.35, 1.0, sin( uTime * ( 0.9 + aSeed.z ) + aSeed.y * 40.0 ) );
    float lowY = smoothstep( 4.5, 0.5, p.y - uCam.y + 1.6 );
    vA = mix( vA, fly * pulse * lowY * 1.4, uNight );
    gl_PointSize = mix( gl_PointSize, min( uPx * 2.2 / max( -mv.z, 0.5 ) + 1.5, 7.0 ), uNight );
  }
}
`;
const MOTE_FRAG = /* glsl */ `
uniform vec3 uCol; uniform float uNight; varying float vA;
void main() {
  vec2 c = gl_PointCoord - 0.5;
  float a = smoothstep( 0.5, 0.15, length( c ) ) * vA;
  vec3 fly = vec3( 1.5, 1.9, 0.35 ) * ( 0.25 + 0.75 * smoothstep( 0.32, 0.0, length( c ) ) );
  gl_FragColor = vec4( mix( uCol * 0.55, fly, uNight ) * a, a );
}
`;

const BIRD_VERT = /* glsl */ `
attribute vec4 aBird; // x: phase along the loop, y: lateral offset, z: height offset, w: flap phase
uniform float uTime; uniform vec3 uFlock; uniform vec3 uHazeColor;
varying float vFog;
void main() {
  float a = uTime * 0.035 + aBird.x;
  vec3 c = uFlock + vec3( cos( a ) * 150.0, aBird.z + sin( a * 3.0 + aBird.w ) * 4.0, sin( a ) * 110.0 );
  vec3 fwd = normalize( vec3( -sin( a ) * 150.0, 0.0, cos( a ) * 110.0 ) );
  vec3 side = vec3( fwd.z, 0.0, -fwd.x );
  c += side * aBird.y;
  float flap = sin( uTime * 7.0 + aBird.w * 6.28 );
  // position.x: -1..1 across the wings, position.z: 0 body .. 1 tail
  vec3 p = c + side * position.x * 1.4 - fwd * position.z * 0.6;
  p.y += abs( position.x ) * flap * 0.6;
  vec4 mv = viewMatrix * vec4( p, 1.0 );
  vFog = clamp( length( mv.xyz ) / 900.0, 0.0, 0.85 );
  gl_Position = projectionMatrix * mv;
}
`;
const BIRD_FRAG = /* glsl */ `
uniform vec3 uHazeColor; uniform vec3 uInk;
varying float vFog;
void main() { gl_FragColor = vec4( mix( uInk, uHazeColor, vFog ), 1.0 ); }
`;

export class Sky {
  readonly group = new THREE.Group();
  private dome: THREE.Mesh;
  private motes: THREE.Points;
  private moteU: Record<string, THREE.IUniform>;
  private cloudU: Record<string, THREE.IUniform>;
  private disposables: { dispose(): void }[] = [];
  private dn: DayNight;
  private celestial: Celestial;
  private birdMesh: THREE.Mesh | null = null;
  /** the cloud sea's day inks, kept so the cycle can relight it every frame */
  private cloudBase: THREE.Color[] = [];

  constructor(seed: number, quality: number, renderer: THREE.WebGLRenderer, dn: DayNight) {
    this.dn = dn;
    // dome
    const dg = new THREE.SphereGeometry(1000, 48, 24);
    const dm = new THREE.ShaderMaterial({
      vertexShader: DOME_VERT, fragmentShader: DOME_FRAG, side: THREE.BackSide, depthWrite: false,
      uniforms: {
        // golden_grass_mechs.png: the whole sky is warm haze, a little deeper and more
        // saturated overhead, palest at the horizon; no blue
        uTop: { value: new THREE.Color(0xf8b465) },
        uMid: { value: new THREE.Color(0xffcf88) },
        uHorizon: { value: new THREE.Color(0xffe2aa) },
        uHazeColor: HAZE.uHazeColor, uSunHazeColor: HAZE.uSunHazeColor, uSunDir: HAZE.uSunDir,
        uSunCore: { value: new THREE.Color(PAL.sunCore) },
        uTownMood: HAZE.uTownMood, uTime: HAZE.uTime,
        uTopBlue: { value: new THREE.Color(PAL.skyTop) },
        uMidBlue: { value: new THREE.Color(0x6fa7c9) },
        uCloudLit: { value: new THREE.Color(0xfff6e2) },
        uCloudShade: { value: new THREE.Color(0x9eabbd).lerp(new THREE.Color(PAL.hazeLight), 0.12) },
        uNight: dn.U.uNight, uDusk: dn.U.uDusk, uSunDisc: dn.U.uSunDisc, uSkyRotInv: dn.U.uSkyRotInv,
      },
    });
    this.dome = new THREE.Mesh(dg, dm);
    this.dome.renderOrder = -100;
    this.dome.frustumCulled = false;
    this.group.add(this.dome);
    this.disposables.push(dg, dm);

    // the planets and moons (night; the giant and the big moon ghost faintly by day)
    this.celestial = new Celestial(dn.U, quality);
    this.group.add(this.celestial.mesh);
    this.disposables.push(this.celestial);

    // cloud sea
    this.cloudU = {
      uTime: HAZE.uTime, uHazeColor: HAZE.uHazeColor, uSunHazeColor: HAZE.uSunHazeColor, uSunDir: HAZE.uSunDir,
      uNight: dn.U.uNight,
      uLit: { value: new THREE.Color(PAL.warmCream).lerp(new THREE.Color(PAL.sunCore), 0.4) },
      uMid: { value: new THREE.Color(PAL.hazeLight).lerp(new THREE.Color(PAL.warmCream), 0.3) },
      uShade: { value: new THREE.Color(PAL.hazeGold).lerp(new THREE.Color(PAL.plum), 0.18) },
    };
    this.cloudBase = [this.cloudU.uLit.value, this.cloudU.uMid.value, this.cloudU.uShade.value].map((c) => (c as THREE.Color).clone());
    const cg = cloudSea(seed, quality);
    const cm = new THREE.ShaderMaterial({ vertexShader: CLOUD_VERT, fragmentShader: CLOUD_FRAG, uniforms: this.cloudU });
    const cloud = new THREE.Mesh(cg, cm);
    cloud.renderOrder = -90;
    cloud.name = 'cloudSea';
    this.group.add(cloud);
    this.disposables.push(cg, cm);

    // distant silhouettes
    const sg = silhouettes(seed, quality);
    const sm = new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide });
    sm.onBeforeCompile = (sh) => {
      sh.uniforms.uTownMood = HAZE.uTownMood;
      sh.uniforms.uHazeColor = HAZE.uHazeColor;
      sh.uniforms.uSkyTint = dn.U.uSkyTint;
      sh.uniforms.uSkyLift = dn.U.uSkyLift;
      sh.uniforms.uNightR = dn.U.uNight;
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nattribute float aForest;\nvarying float vForest;\nvarying vec3 vWP;\nuniform float uTownMood;')
        // in town the backdrop sits lower, so no bright peak stands up over the street-end skyline
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvForest = aForest;\nvWP = ( modelMatrix * vec4( position, 1.0 ) ).xyz;\ntransformed.y = mix( transformed.y, -20.0 + ( transformed.y + 20.0 ) * 0.62, uTownMood );');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying float vForest;\nvarying vec3 vWP;\nuniform float uTownMood;\nuniform vec3 uHazeColor;\n' + RELIGHT_GLSL + FOREST_GLSL)
        .replace('#include <color_fragment>', '#include <color_fragment>\n  if ( vForest > 0.01 ) diffuseColor.rgb = forestPaint( diffuseColor.rgb, vWP, vForest );\n  if ( uTownMood > 0.001 ) {\n    float lum = dot( diffuseColor.rgb, vec3( 0.299, 0.587, 0.114 ) );\n    vec3 dsat = mix( diffuseColor.rgb, vec3( lum ), 0.6 );\n    vec3 pale = mix( uHazeColor * 0.82, vec3( 0.5, 0.58, 0.7 ), 0.5 );\n    float low = 1.0 - smoothstep( -40.0, 140.0, vWP.y - cameraPosition.y );\n    diffuseColor.rgb = mix( diffuseColor.rgb, mix( dsat, pale, 0.5 + 0.3 * low ), uTownMood );\n  }\n  diffuseColor.rgb = skyRelight( diffuseColor.rgb );');
    };
    sm.customProgramCacheKey = () => 'ranges_forest';
    const sil = new THREE.Mesh(sg, sm);
    sil.renderOrder = -80;
    this.group.add(sil);
    this.disposables.push(sg, sm);

    // soft mist bands between the range layers (low, horizontal, layered)
    const mg0 = mistBands(seed, quality);
    const mm0 = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, side: THREE.DoubleSide });
    mm0.onBeforeCompile = (sh) => {
      sh.uniforms.uHazeColor = HAZE.uHazeColor;
      sh.uniforms.uSkyTint = dn.U.uSkyTint;
      sh.uniforms.uSkyLift = dn.U.uSkyLift;
      sh.uniforms.uNightR = dn.U.uNight;
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nuniform vec3 uHazeColor;\n' + RELIGHT_GLSL)
        .replace('#include <color_fragment>', '#include <color_fragment>\n  // night mist glows a soft slate-teal between the range layers\n  diffuseColor.rgb = mix( skyRelight( diffuseColor.rgb ), vec3( 0.07, 0.11, 0.17 ), min( uSkyLift * 2.2, 1.0 ) );');
    };
    mm0.customProgramCacheKey = () => 'mist_relight';
    const mist = new THREE.Mesh(mg0, mm0);
    mist.renderOrder = -79;
    mist.name = 'mistBands';
    this.group.add(mist);
    this.disposables.push(mg0, mm0);

    // dust motes in the sun
    const n = [160, 360, 600, 900][Math.max(0, Math.min(3, quality))];
    const mg = new THREE.BufferGeometry();
    const seeds = new Float32Array(n * 3);
    const j = new Jit(seed ^ 0x3073);
    for (let i = 0; i < n * 3; i++) seeds[i] = j.next();
    mg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(n * 3), 3));
    mg.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 3));
    this.moteU = {
      uTime: HAZE.uTime, uCam: { value: new THREE.Vector3() }, uBox: { value: 30 },
      uPx: { value: renderer.getPixelRatio() * 10 }, uCol: { value: new THREE.Color(PAL.sunCore) }, uSunDirM: HAZE.uSunDir,
      uNight: dn.U.uNight,
    };
    const mm = new THREE.ShaderMaterial({
      vertexShader: MOTE_VERT, fragmentShader: MOTE_FRAG, uniforms: this.moteU,
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending,
    });
    this.motes = new THREE.Points(mg, mm);
    this.motes.frustumCulled = false;
    this.motes.renderOrder = 50;
    this.group.add(this.motes);
    this.disposables.push(mg, mm);
    this.birds(seed, quality);
  }

  private birds(seed: number, quality: number) {
    if (quality <= 0) return;
    // one wing-pair silhouette, instanced into a loose flock wheeling over the valley
    const g = new THREE.InstancedBufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute([-1, 0, 0.3, 0, 0, 0, 0, 0, 0.8, 0, 0, 0, 1, 0, 0.3, 0, 0, 0.8], 3));
    const n = quality >= 2 ? 16 : 9;
    const a = new Float32Array(n * 4);
    const j = new Jit(seed ^ 0xb1d);
    for (let i = 0; i < n; i++) {
      a[i * 4] = (i % 2 ? -1 : 1) * Math.ceil(i / 2) * 0.012 + j.range(-0.004, 0.004);
      a[i * 4 + 1] = (i % 2 ? -1 : 1) * Math.ceil(i / 2) * 3.0 + j.range(-1, 1);
      a[i * 4 + 2] = j.range(-4, 4);
      a[i * 4 + 3] = j.next();
    }
    g.setAttribute('aBird', new THREE.InstancedBufferAttribute(a, 4));
    g.instanceCount = n;
    const sun = HAZE.uSunDir.value as THREE.Vector3;
    const m = new THREE.ShaderMaterial({
      vertexShader: BIRD_VERT, fragmentShader: BIRD_FRAG, side: THREE.DoubleSide,
      uniforms: {
        uTime: HAZE.uTime, uHazeColor: HAZE.uHazeColor, uInk: { value: new THREE.Color(PAL.inkDark).lerp(new THREE.Color(PAL.plum), 0.3) },
        uFlock: { value: new THREE.Vector3(sun.x * 220, 95, sun.z * 220) },
      },
    });
    const mesh = new THREE.Mesh(g, m);
    mesh.frustumCulled = false;
    mesh.name = 'birds';
    this.birdMesh = mesh;
    this.group.add(mesh);
    this.disposables.push(g, m);
  }

  update(camPos: THREE.Vector3) {
    this.dome.position.copy(camPos);
    (this.moteU.uCam.value as THREE.Vector3).copy(camPos);
    const n = this.dn.night, k = this.dn.dusk;
    // the cloud sea: rose and violet at sunset, cool moonlit blues at night
    const [lit, mid, shade] = [this.cloudU.uLit.value, this.cloudU.uMid.value, this.cloudU.uShade.value] as THREE.Color[];
    lit.copy(this.cloudBase[0]).lerp(CLOUD_DUSK[0], k * 0.8).lerp(CLOUD_NIGHT[0], n);
    mid.copy(this.cloudBase[1]).lerp(CLOUD_DUSK[1], k * 0.8).lerp(CLOUD_NIGHT[1], n);
    shade.copy(this.cloudBase[2]).lerp(CLOUD_DUSK[2], k * 0.8).lerp(CLOUD_NIGHT[2], n);
    if (this.birdMesh) this.birdMesh.visible = n < 0.5;
  }

  dispose() { for (const d of this.disposables) d.dispose(); }
}

const CLOUD_DUSK = [new THREE.Color(0xffb48c), new THREE.Color(0xd88b8e), new THREE.Color(0x6e5277)];
const CLOUD_NIGHT = [new THREE.Color(0x2a4258), new THREE.Color(0x0f1c2a), new THREE.Color(0x040810)];
/** Backdrop relight for the day-baked ranges and mist: multiply by the sky tint, lift by the haze. */
const RELIGHT_GLSL = /* glsl */ `
uniform vec3 uSkyTint; uniform float uSkyLift; uniform float uNightR;
vec3 skyRelight( vec3 c ) {
  float l = dot( c, vec3( 0.3, 0.59, 0.11 ) );
  vec3 d = c * uSkyTint + uHazeColor * l * uSkyLift * ( 1.0 - uNightR );
  // night aerial perspective: the day's pre-fogged layers are re-inked by how hazed they were,
  // near walls near-black indigo, each layer back a step lighter toward the glowing horizon
  float n = smoothstep( 0.06, 0.72, l );
  vec3 nc = mix( vec3( 0.01, 0.014, 0.03 ), vec3( 0.1, 0.13, 0.22 ), n * n );
  return mix( d, nc, uNightR );
}
`;

// ------------------------------------------------------------------ cloud sea
/** Billowy cumulus floor: an indexed grid (smooth normals) that is fine near the islands and
 *  coarse toward the horizon; lighting baked into a 0..1 term that the shader posterizes. */
function cloudSea(seed: number, quality: number) {
  const R = 1300, n = quality >= 2 ? 58 : 48;
  const Y = -112;
  const N1 = n + 1;
  const P = new Float32Array(N1 * N1 * 3);
  const warp = (u: number) => Math.sign(u) * Math.pow(Math.abs(u), 1.7) * R;
  const hAt = (x: number, z: number) => {
    const n1 = valueNoise(seed ^ 0xc10d, x / 150, z / 150), n2 = valueNoise(seed ^ 0xc10e, x / 52, z / 52), n3 = valueNoise(seed ^ 0xc10f, x / 19, z / 19);
    return Y + 46 * Math.pow(n1, 2.2) + 24 * Math.pow(n2, 2.4) + 6 * n3;
  };
  for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) {
    const x = warp(i / n * 2 - 1), z = warp(j / n * 2 - 1);
    const k = (j * N1 + i) * 3;
    P[k] = x; P[k + 1] = hAt(x, z); P[k + 2] = z;
  }
  const sun = HAZE.uSunDir.value as THREE.Vector3;
  const L = new Float32Array(N1 * N1 * 3);
  const nrm = new THREE.Vector3();
  for (let j = 0; j <= n; j++) for (let i = 0; i <= n; i++) {
    const x = P[(j * N1 + i) * 3], z = P[(j * N1 + i) * 3 + 2];
    const e = 3;
    nrm.set(hAt(x - e, z) - hAt(x + e, z), 2 * e, hAt(x, z - e) - hAt(x, z + e)).normalize();
    const lit = Math.max(0, nrm.dot(sun));
    const y = P[(j * N1 + i) * 3 + 1];
    const k = (j * N1 + i) * 3;
    L[k] = lit; L[k + 1] = (y - Y) / 70; L[k + 2] = 0;
  }
  const idx: number[] = [];
  for (let j = 0; j < n; j++) for (let i = 0; i < n; i++) {
    const a = j * N1 + i, b = a + 1, c = a + N1, d = c + 1;
    if (Math.hypot(P[a * 3], P[a * 3 + 2]) > R * 1.02) continue;
    idx.push(a, c, d, a, d, b);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(P, 3));
  g.setAttribute('color', new THREE.BufferAttribute(L, 3));
  g.setIndex(idx);
  g.computeBoundingSphere();
  return g;
}

// ------------------------------------------------------------------ silhouettes
/** Distant floating islands and buttes as tiny low-poly volumes, flat-coloured and pre-fogged
 *  per depth layer (they read right from the high camera too, unlike flat cards). */
function silhouettes(seed: number, quality: number) {
  const layers = quality <= 0 ? [400, 800] : quality === 1 ? [270, 420, 640] : [265, 410, 610, 880];
  const fogs = quality <= 0 ? [0.5, 0.75] : quality === 1 ? [0.2, 0.4, 0.58] : [0.2, 0.4, 0.56, 0.7];
  const pos: number[] = [], col: number[] = [];
  const j = new Jit(seed ^ 0x51e7);
  const sun = HAZE.uSunDir.value as THREE.Vector3;
  const hazeC = HAZE.uHazeColor.value as THREE.Color, sunHaze = HAZE.uSunHazeColor.value as THREE.Color;
  const sideC = new THREE.Color(PAL.shadowTeal).lerp(new THREE.Color(PAL.underside), 0.35);
  const topC = new THREE.Color(PAL.grassGold).lerp(new THREE.Color(PAL.grassGreenDeep), 0.35);
  const rockC = new THREE.Color(PAL.brick).lerp(new THREE.Color(PAL.shadowTeal), 0.45);
  const hz = new THREE.Color(), cS = new THREE.Color(), cT = new THREE.Color(), cL = new THREE.Color();
  const push = (p: number[], c: THREE.Color) => { pos.push(p[0], p[1], p[2]); col.push(c.r, c.g, c.b); };
  const tri = (a: number[], b: number[], c: number[], k: THREE.Color) => { push(a, k); push(b, k); push(c, k); };
  layers.forEach((D, li) => {
    const count = 8 + li * 3;
    for (let k = 0; k < count; k++) {
      const ang = (k / count) * Math.PI * 2 + j.range(-0.25, 0.25) + li * 0.7;
      const ux = Math.cos(ang), uz = Math.sin(ang);
      const dist = D * j.range(0.9, 1.1);
      const cx = ux * dist, cz = uz * dist;
      const sunAmt = Math.pow(Math.max(0, (ux * sun.x + uz * sun.z) / Math.hypot(sun.x, sun.z)), 5);
      hz.copy(hazeC).lerp(sunHaze, sunAmt * 0.8);
      const f = fogs[li];
      const mesa = j.next() < 0.3;
      cS.copy(mesa ? rockC : sideC).lerp(hz, f);
      cT.copy(topC).lerp(hz, f * 0.85);
      cL.copy(cS).lerp(cT, 0.5);
      const W = dist * j.range(0.05, 0.11);
      const n = 7;
      const a0 = j.range(0, 6.28);
      const rr = Array.from({ length: n }, () => j.range(0.75, 1.05));
      const ring = (r: number, y: number, dx = 0, dz = 0) => rr.map((q, i) => {
        const a = a0 + (i / n) * Math.PI * 2;
        return [cx + dx + Math.cos(a) * r * q, y, cz + dz + Math.sin(a) * r * q * 0.8];
      });
      const band = (A: number[][], B: number[][], c: THREE.Color) => {
        for (let i = 0; i < n; i++) {
          const i2 = (i + 1) % n;
          tri(A[i], B[i], B[i2], c); tri(A[i], B[i2], A[i2], c);
        }
      };
      const cap = (A: number[][], y: number, c: THREE.Color, dx = 0, dz = 0) => {
        for (let i = 0; i < n; i++) tri([cx + dx, y, cz + dz], A[(i + 1) % n], A[i], c);
      };
      if (mesa) {
        const top = j.range(-55, 0) + li * 8;
        const R = W * j.range(0.9, 1.3);
        const T = ring(R, top), M = ring(R * 1.06, top - R * 0.35), B = ring(R * 1.45, -130);
        cap(T, top, cT);
        band(T, M, cS); band(M, B, cL.copy(cS).multiplyScalar(0.93));
      } else {
        const top = j.range(10, 75) + li * 16;
        const depth = W * j.range(0.55, 0.9);
        let prev = ring(W, top);
        cap(prev, top + W * 0.03, cT);
        const lip = ring(W * 1.02, top - W * 0.08);
        band(prev, lip, cL);
        prev = lip;
        const tiers = 4;
        let dx = 0, dz = 0;
        for (let s = 1; s <= tiers; s++) {
          const t = s / tiers;
          dx += j.range(-0.08, 0.08) * W; dz += j.range(-0.08, 0.08) * W;
          const r = W * (1 - Math.pow(t, 1.25) * 0.82) * j.range(0.92, 1.0);
          const ring2 = ring(r, top - depth * t, dx, dz);
          band(prev, ring2, cS);
          prev = ring2;
        }
        for (let i = 0; i < n; i++) tri(prev[i], [cx + dx, top - depth * 1.35, cz + dz], prev[(i + 1) % n], cS);
      }
    }
  });
  const forest: number[] = new Array(pos.length / 3).fill(0);
  ranges(seed, quality, pos, col, forest);
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  g.setAttribute('aForest', new THREE.Float32BufferAttribute(forest, 1));
  g.computeBoundingSphere();
  return g;
}

/**
 * Layered mountain ranges around the whole sky, each layer pre-fogged toward the haze and
 * dissolving into mist at its foot (golden_grass_mechs.png: stacked valley walls, darkest
 * and most forested in front, nearly sky at the back). The ranges part toward the sun so
 * the light pours down a valley, and their crests carry a fine forest fringe.
 */
const tmpCT = new THREE.Color(), tmpCT2 = new THREE.Color();
function ranges(seed: number, quality: number, pos: number[], col: number[], forest: number[]) {
  const L = quality <= 0
    ? [{ D: 260, H: 140, f: 0.2 }, { D: 520, H: 220, f: 0.5 }, { D: 900, H: 280, f: 0.75 }]
    : [{ D: 215, H: 82, f: 0.12 }, { D: 330, H: 150, f: 0.44 }, { D: 500, H: 215, f: 0.62 }, { D: 720, H: 265, f: 0.75 }, { D: 1000, H: 300, f: 0.85 }];
  const N = quality <= 0 ? 160 : quality === 1 ? 240 : 300;
  const sun = HAZE.uSunDir.value as THREE.Vector3;
  const sunAz = Math.atan2(sun.z, sun.x);
  const hazeC = HAZE.uHazeColor.value as THREE.Color, sunHaze = HAZE.uSunHazeColor.value as THREE.Color;
  const near = new THREE.Color(0x3a605c);
  const airPeach = new THREE.Color(0xf6d6b2), airBlue = new THREE.Color(0xc4cdd6), airC = new THREE.Color();
  const lit = new THREE.Color(PAL.olive).lerp(new THREE.Color(PAL.grassGold), 0.35);
  const hz = new THREE.Color(), cTop = new THREE.Color(), cMid = new THREE.Color(), cBot = new THREE.Color();
  const cloudFoot = new THREE.Color(PAL.warmCream).lerp(new THREE.Color(PAL.hazeLight), 0.5);
  const tops: number[][] = [], cols: THREE.Color[][] = [];
  L.forEach((ly, li) => {
    tops.length = 0; cols.length = 0;
    const s2 = seed ^ (0x7a11 + li * 977);
    const ridge = (a: number) => {
      const x = Math.cos(a) * ly.D, z = Math.sin(a) * ly.D;
      let h = 0, amp = 1, fr = 1 / (ly.D * (0.38 + 0.12 * li)), tot = 0;
      // domain warp so no two stretches of crest repeat (no twin-peak W)
      const wx = (valueNoise(s2 ^ 0x77, x / 90, z / 90) - 0.5) * ly.D * 0.6, wz = (valueNoise(s2 ^ 0x78, x / 90, z / 90) - 0.5) * ly.D * 0.6;
      for (let o = 0; o < 4; o++) {
        // soft rounded ridges (valley walls), not alpine spikes
        const v = valueNoise(s2 + o * 31, (x + wx) * fr, (z + wz) * fr);
        const n = o === 0 ? v : 1 - Math.abs(v * 2 - 1);
        h += (o === 0 ? n : n * n) * amp; tot += amp; amp *= o === 0 ? 0.45 : 0.38; fr *= 2.07;
      }
      h /= tot;
      // the valley opening toward the sun
      let da = a - sunAz; da = Math.atan2(Math.sin(da), Math.cos(da));
      const valley = 1 - 0.62 * Math.exp(-(da * da) / (0.5 * 0.5));
      return -20 + ly.H * (0.35 + 0.9 * h) * valley;
    };
    // serrated forest on the near crests: alternate samples carry a pointed crown, gathered
    // into forested stretches with bare saddles between (golden_grass_mechs.png's tree lines)
    const forestAt = (a: number) => li < 2 ? THREE.MathUtils.smoothstep(valueNoise(s2 ^ 0xf0, Math.cos(a) * ly.D / 70 + 3, Math.sin(a) * ly.D / 70 + 3), 0.4, 0.56) : 0;
    const fringe = (i: number, a: number) => {
      const f = forestAt(a);
      if (f <= 0) return 0;
      // irregular crowns: per-sample hash height, softened by a slow noise -- no regular teeth
      const hs = Math.abs(Math.sin(i * 12.9898 + li * 78.233) * 43758.5453) % 1;
      const v = valueNoise(s2 ^ 0xf1, Math.cos(a) * ly.D / 14, Math.sin(a) * ly.D / 14);
      const tall = (li === 0 ? 4.5 : 6) * v * (hs < 0.4 ? hs * 0.6 : hs);
      return f * ((li === 0 ? 2.5 : 3.5) + tall * 0.3);
    };
    for (let i = 0; i <= N; i++) {
      const a = (i / N) * Math.PI * 2;
      const D = ly.D * (1 + (valueNoise(s2 ^ 0x11, Math.cos(a) * 2.5 + 7, Math.sin(a) * 2.5 + 7) - 0.5) * 0.16);
      const x = Math.cos(a), z = Math.sin(a);
      const h = ridge(a) + fringe(i, a);
      const dh = (ridge(a + 0.03) - ridge(a - 0.03)) * 0.5;
      let da = a - sunAz; da = Math.atan2(Math.sin(da), Math.cos(da));
      const sunAmt = Math.pow(Math.max(0, Math.cos(da)), 5) * Math.max(0.2, ly.f);
      // air perspective: peach in the middle distance, cooling to blue-grey at the back
      airC.copy(airPeach).lerp(airBlue, THREE.MathUtils.smoothstep(ly.f, 0.45, 0.85));
      hz.copy(hazeC).lerp(airC, Math.min(1, ly.f * 1.15) * (1 - sunAmt * 0.6)).lerp(sunHaze, sunAmt * 0.85);
      // flank facing the sun catches warm light, the other flank stays teal
      const side = 0.6 * Math.tanh(dh * 0.12 * Math.sin(da));
      cTop.copy(near).lerp(lit, 0.12 + 0.28 * side).lerp(hz, ly.f);
      // forested gullies and spurs: soft darker/lighter vertical breakup on the faces
      const gul = valueNoise(s2 ^ 0x6a1, Math.cos(a) * ly.D / 40, Math.sin(a) * ly.D / 40);
      cTop.multiplyScalar(0.93 + 0.14 * gul * (1 - ly.f));
      // forested near walls read darker under the tree line
      cTop.multiplyScalar(1 - 0.18 * forestAt(a) * (1 - ly.f));
      cMid.copy(cTop).lerp(hz, 0.5);
      // the foot dissolves into the cloud-sea tone, so seen from above the near walls meet the
      // cloud floor without a hard arc
      cBot.copy(hz).lerp(cloudFoot, 0.7);
      tops.push([x * D, h, z * D, h - fringe(i, a)]);
      cols.push([cTop.clone(), cMid.clone(), cBot.clone()]);
    }
    let fA = 0, fB = 0;
    const FR = [1, 1, 0.55, 0];
    let rowK = 0, useB = false;
    const push = (p: number[], c: THREE.Color) => { pos.push(p[0], p[1], p[2]); col.push(c.r, c.g, c.b); forest.push((useB ? fB : fA) * FR[rowK]); };
    for (let i = 0; i < N; i++) {
      // painted forest mottling strength: strongest on the near forested walls
      const cov = (a: number) => (1 - ly.f) * (li < 2 ? 0.3 + 0.25 * forestAt(a) : 0.3);
      fA = cov((i / N) * Math.PI * 2); fB = cov(((i + 1) / N) * Math.PI * 2);
      const A = tops[i], B = tops[i + 1];
      const cA = cols[i], cB = cols[i + 1];
      // crest -> shoulder -> mist line -> foot in the haze: a soft vertical fade
      // only the crest carries the forest comb; lower rows hang off the smooth ridge
      const lv = (P: number[], t: number) => (t === 0 ? [P[0], P[1], P[2]] : [P[0], P[3] - (P[3] + 150) * t, P[2]]);
      const T = [0, 0.12, 0.38, 1];
      const cc = (C: THREE.Color[], k: number, out: THREE.Color) =>
        k === 0 ? out.copy(C[0]) : k === 1 ? out.copy(C[0]).lerp(C[1], 0.3) : k === 2 ? out.copy(C[1]).lerp(C[2], 0.55) : out.copy(C[2]);
      for (let k = 0; k < 3; k++) {
        const a0 = lv(A, T[k]), a1 = lv(A, T[k + 1]), b0 = lv(B, T[k]), b1 = lv(B, T[k + 1]);
        const ca0 = cc(cA, k, new THREE.Color()), ca1 = cc(cA, k + 1, new THREE.Color()), cb0 = cc(cB, k, new THREE.Color()), cb1 = cc(cB, k + 1, new THREE.Color());
        const P = (p: number[], c: THREE.Color, r: number, b: boolean) => { rowK = r; useB = b; push(p, c); };
        P(a0, ca0, k, false); P(a1, ca1, k + 1, false); P(b0, cb0, k, true);
        P(b0, cb0, k, true); P(a1, ca1, k + 1, false); P(b1, cb1, k + 1, true);
      }
      // tree spikes on the near forested crests: individual conifer-ish crowns of random
      // height, width and spacing standing on the ridge (a real tree line, not a texture)
      if (li < 2) {
        const fa = forestAt((i / N) * Math.PI * 2);
        const hs = Math.abs(Math.sin(i * 91.7 + li * 13.1) * 43758.5453) % 1;
        const nT = fa > 0.15 ? (hs < 0.3 ? 1 : hs < 0.85 ? 2 : 3) : (hs < 0.12 ? 1 : 0);
        for (let t = 0; t < nT; t++) {
          const u = (t + 0.2 + 0.6 * ((hs * (t + 3) * 7.31) % 1)) / Math.max(1, nT);
          const bx = A[0] + (B[0] - A[0]) * u, bz = A[2] + (B[2] - A[2]) * u, by = A[3] + (B[3] - A[3]) * u;
          const tx = B[0] - A[0], tz = B[2] - A[2], tl = Math.hypot(tx, tz) || 1;
          const r2 = (hs * (t + 1) * 17.13) % 1;
          const w = (li === 0 ? 1.6 : 2.2) * (0.7 + 0.6 * r2);
          const ht = (li === 0 ? 5 : 7) * (0.55 + 0.9 * ((r2 * 5.7) % 1)) * (0.5 + 0.5 * Math.max(fa, 0.4));
          const cT = tmpCT.copy(cA[0]).multiplyScalar(0.84);
          rowK = 0; useB = false;
          push([bx - (tx / tl) * w, by - 1.5, bz - (tz / tl) * w], cT);
          push([bx + (tx / tl) * w, by - 1.5, bz + (tz / tl) * w], cT);
          push([bx, by + ht, bz], tmpCT2.copy(cT).multiplyScalar(1.06));
        }
      }
    }
  });
}

/** Painted forest on the near range faces: dark teal canopy masses with lighter, sunlit tops. */
const FOREST_GLSL = /* glsl */ `
float fh3( vec3 p ) { return fract( sin( dot( p, vec3( 127.1, 311.7, 74.7 ) ) ) * 43758.5453 ); }
float fn3( vec3 p ) {
  vec3 i = floor( p ), f = fract( p );
  f = f * f * ( 3.0 - 2.0 * f );
  return mix( mix( mix( fh3( i ), fh3( i + vec3( 1, 0, 0 ) ), f.x ), mix( fh3( i + vec3( 0, 1, 0 ) ), fh3( i + vec3( 1, 1, 0 ) ), f.x ), f.y ),
              mix( mix( fh3( i + vec3( 0, 0, 1 ) ), fh3( i + vec3( 1, 0, 1 ) ), f.x ), mix( fh3( i + vec3( 0, 1, 1 ) ), fh3( i + vec3( 1, 1, 1 ) ), f.x ), f.y ), f.z );
}
vec3 forestPaint( vec3 c, vec3 wp, float k ) {
  vec3 q = wp * vec3( 1.0, 1.25, 1.0 );
  float n = fn3( q / 5.0 ) * 0.65 + fn3( q / 2.1 ) * 0.35;
  float above = fn3( ( q + vec3( 0.0, 2.2, 0.0 ) ) / 5.0 ) * 0.65 + fn3( ( q + vec3( 0.0, 2.2, 0.0 ) ) / 2.1 ) * 0.35;
  float canopy = smoothstep( 0.3, 0.7, n );
  float top = smoothstep( 0.0, 0.25, n - above ) * canopy;
  vec3 dark = c * vec3( 0.52, 0.7, 0.68 );
  vec3 lit = c * vec3( 1.12, 1.1, 0.98 );
  vec3 f = mix( c * vec3( 0.86, 0.95, 0.9 ), dark, canopy );
  f = mix( f, lit * vec3( 0.95, 1.04, 0.92 ), top * 0.45 );
  return mix( c, f, k );
}
`;

/** Horizontal mist bands: open rings of soft haze between the range layers. */
function mistBands(seed: number, quality: number) {
  const bands = quality <= 0 ? [{ D: 200, y: -30, h: 45 }, { D: 420, y: -20, h: 60 }]
    : [{ D: 165, y: -26, h: 36 }, { D: 270, y: -18, h: 50 }, { D: 410, y: -8, h: 62 }, { D: 610, y: 4, h: 80 }, { D: 860, y: 14, h: 95 }];
  const S = quality <= 0 ? 48 : 96;
  const pos: number[] = [], col: number[] = [];
  const hazeC = new THREE.Color(PAL.hazeLight), sunHaze = HAZE.uSunHazeColor.value as THREE.Color;
  const sun = HAZE.uSunDir.value as THREE.Vector3;
  const sunAz = Math.atan2(sun.z, sun.x);
  const c = new THREE.Color();
  bands.forEach((b, bi) => {
    const s2 = seed ^ (0x3157 + bi * 131);
    const rows = [0, 0.3, 0.65, 1];
    const alpha = [0, 0.78, 0.45, 0];
    for (let i = 0; i < S; i++) {
      for (let r = 0; r < 3; r++) {
        const quad: number[][] = [];
        for (const [ii, rr] of [[i, r], [i + 1, r], [i + 1, r + 1], [i, r + 1]]) {
          const a = (ii / S) * Math.PI * 2;
          const wob = valueNoise(s2, Math.cos(a) * 3 + 5, Math.sin(a) * 3 + 5);
          const D = b.D * (1 + (wob - 0.5) * 0.1);
          const top = b.y + b.h * (0.6 + 0.8 * valueNoise(s2 ^ 0x9, Math.cos(a) * 5 + 2, Math.sin(a) * 5 + 2));
          const y = b.y + (top - b.y) * rows[rr];
          let da = a - sunAz; da = Math.atan2(Math.sin(da), Math.cos(da));
          const sunAmt = Math.pow(Math.max(0, Math.cos(da)), 3);
          c.copy(hazeC).lerp(sunHaze, sunAmt * 0.7);
          const al = alpha[rr] * (0.55 + 0.45 * wob) * (1 - bi * 0.08);
          quad.push([Math.cos(a) * D, y, Math.sin(a) * D, c.r, c.g, c.b, al]);
        }
        for (const k of [0, 1, 2, 0, 2, 3]) {
          const v = quad[k];
          pos.push(v[0], v[1], v[2]);
          col.push(v[3], v[4], v[5], v[6]);
        }
      }
    }
  });
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('color', new THREE.Float32BufferAttribute(col, 4));
  g.computeBoundingSphere();
  return g;
}
